/**
 * Transforms a single spacer choice from API format to component format
 * @param {Object} choice - The spacer choice from API
 * @returns {Object|null} Transformed spacer data or null if invalid
 */
export const transformSpacerChoiceToSpacerData = (choice) => {
  if (!choice) return null;

  // Filter out choices without valid IDs
  if (!choice.id && choice.id !== 0) return null;

  const metaDataItems = choice.metaDataItems || [];

  // Extract metadata values (case-insensitive and trimmed matching)
  const getMetaValue = (name) => {
    const meta = metaDataItems.find(
      (item) => item.name?.toLowerCase().trim() === name.toLowerCase().trim()
    );
    return meta?.value ?? null;
  };

  const spacerTitle = getMetaValue('spacer title');
  const spacerPosition = getMetaValue('spacer position');
  const defaultWidthStr = getMetaValue('default width (in.)');
  const defaultHeightStr = getMetaValue('default height (in.)');

  // Parse dimensions - null if not provided or not a valid number
  const defaultWidth = defaultWidthStr ? parseFloat(defaultWidthStr) : null;
  const defaultHeight = defaultHeightStr ? parseFloat(defaultHeightStr) : null;

  // Generate ID from spacer title (convert to snake_case)
  const id = spacerTitle
    ? spacerTitle.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    : `spacer_${choice.id}`;

  // Use shortLabel or spacerTitle for display label
  const label = choice.shortLabel || spacerTitle || '';

  return {
    id,
    choiceId: choice.id,
    label,
    spacerPosition: spacerPosition?.toLowerCase().trim() || 'base',
    spacerTitle: spacerTitle || id,
    defaultWidth: Number.isNaN(defaultWidth) ? null : defaultWidth,
    defaultHeight: Number.isNaN(defaultHeight) ? null : defaultHeight,
    internalName: choice.internalName,
    mediaUrl: choice.mediaUrl
  };
};

/**
 * Transforms array of spacer choices from API format to component format
 * @param {Array} choices - Array of spacer choices from API
 * @returns {Array} Array of transformed spacer data
 */
export const transformSpacerChoices = (choices) => {
  if (!choices || !Array.isArray(choices)) return [];

  return choices
    .filter((choice) => choice.isActive) // Only include active spacers
    .map(transformSpacerChoiceToSpacerData)
    .filter(Boolean); // Remove any null results
};

/**
 * Get spacer types filtered by category (wall or base)
 * @param {Array} spacerTypes - Array of transformed spacer types
 * @param {string} category - 'wall' or 'base'
 * @returns {Array} Array of spacer type objects available for that category
 */
export const getSpacerTypesByCategory = (spacerTypes, category) => {
  if (!spacerTypes || !Array.isArray(spacerTypes)) return [];

  const normalizedCategory = category?.toLowerCase().trim();
  return spacerTypes.filter((spacer) => {
    const positions = spacer.spacerPosition.split(',').map((pos) => pos.trim().toLowerCase());
    return positions.includes(normalizedCategory);
  });
};

/**
 * Get a specific spacer type by ID from the spacer types array
 * @param {Array} spacerTypes - Array of transformed spacer types
 * @param {string} typeId - The spacer type ID
 * @returns {Object|undefined} The spacer type object or undefined
 */
export const getSpacerTypeById = (spacerTypes, typeId) => {
  if (!spacerTypes || !Array.isArray(spacerTypes)) return undefined;
  return spacerTypes.find((type) => type.id === typeId);
};

/**
 * Get a specific spacer type by ChoiceId from the spacer types array
 * @param {Array} spacerTypes - Array of transformed spacer types
 * @param {number|string} choiceId - The spacer's ChoiceId
 * @returns {Object|undefined} The spacer type object or undefined
 */
export const getSpacerTypeByChoiceId = (spacerTypes, choiceId) => {
  if (!spacerTypes || !Array.isArray(spacerTypes) || !choiceId) return undefined;
  // Use == for type coercion since choiceId may be string or number
  // eslint-disable-next-line eqeqeq
  return spacerTypes.find((type) => type.choiceId == choiceId);
};

/**
 * Check if a spacer type allows custom dimensions (no default values)
 * @param {Array} spacerTypes - Array of transformed spacer types
 * @param {string} typeId - The spacer type ID
 * @returns {boolean} Whether dimensions are customizable
 */
export const isSpacerCustomizable = (spacerTypes, typeId) => {
  const spacerType = getSpacerTypeById(spacerTypes, typeId);
  return spacerType?.defaultWidth === null && spacerType?.defaultHeight === null;
};
